// AgriLinkVerticalFarmingHub.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSeedling,
  faIndustry,
  faTint,
  faLeaf,
  faBoxOpen,
  faChartLine,
  faGraduationCap,
  faHeadset,
  faRupeeSign,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

/**
 * AgriLinkVerticalFarmingHub
 * - Hub page for vertical farming use cases.
 * - Each portlet expands in-place and renders a working dummy sub-component.
 */

const Portlet = ({ icon, title, desc, expanded, onToggle, children }) => {
  return (
    <div className="bg-gray-600 border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition flex flex-col items-center p-4">
      <button
        onClick={onToggle}
        className="py-4 px-6 flex flex-col items-center gap-3 rounded-xl bg-green-700 hover:bg-green-800 transition w-auto"
        aria-expanded={expanded}
      >
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-lg bg-green-50 text-green-600 flex items-center justify-center text-xl">
            <FontAwesomeIcon icon={icon} />
          </div>
        </div>
        <div className="flex-1">
          <div className="flex justify-between items-start gap-2">
            <div>
              <h3 className="text-lg font-semibold text-yellow-200">{title}</h3>
              <p className="text-sm text-white mt-1">{desc}</p>
            </div>
            <div className="text-xs text-orange-300">
              {expanded ? "Collapse" : "Open"}
            </div>
          </div>
        </div>
      </button>
      {expanded && (
        <div className="p-5 border-t border-gray-100 bg-green-50/40">
          {children}
        </div>
      )}
    </div>
  );
};

/* ----------------- Subcomponents ----------------- */

/* 1. Crop Planner */
const CropPlannerPortlet = () => {
  const [crops, setCrops] = useState([
    { id: 1, crop: "Lettuce", cycle: "30 days", yield: "20kg" },
    { id: 2, crop: "Spinach", cycle: "25 days", yield: "18kg" },
  ]);
  return (
    <div className="space-y-2">
      {crops.map((c) => (
        <div
          key={c.id}
          className="p-3 bg-white rounded-lg shadow flex justify-between"
        >
          <div>
            <h4 className="font-semibold">{c.crop}</h4>
            <p className="text-sm text-gray-500">{c.cycle}</p>
          </div>
          <div className="text-green-700 font-semibold">{c.yield}</div>
        </div>
      ))}
    </div>
  );
};

/* 2. IoT Monitoring */
const IoTPortlet = () => {
  const [sensors] = useState([
    { id: 1, type: "Temperature", value: "22°C" },
    { id: 2, type: "Humidity", value: "60%" },
    { id: 3, type: "pH Level", value: "6.5" },
  ]);
  return (
    <ul className="space-y-2">
      {sensors.map((s) => (
        <li
          key={s.id}
          className="bg-white rounded p-3 flex justify-between items-center"
        >
          <span>{s.type}</span>
          <span className="text-green-700 font-semibold">{s.value}</span>
        </li>
      ))}
    </ul>
  );
};

/* 3. Nutrient Management */
const NutrientPortlet = () => {
  const [nutrients] = useState([
    { id: 1, name: "Nitrogen", level: "Optimal" },
    { id: 2, name: "Phosphorus", level: "Low" },
    { id: 3, name: "Potassium", level: "Optimal" },
  ]);
  return (
    <table className="w-full bg-white rounded">
      <thead>
        <tr className="bg-green-100 text-sm">
          <th className="p-2 text-left">Nutrient</th>
          <th className="p-2 text-left">Status</th>
        </tr>
      </thead>
      <tbody>
        {nutrients.map((n) => (
          <tr key={n.id} className="border-b">
            <td className="p-2 text-green-700">{n.name}</td>
            <td className="p-2 text-green-700">{n.level}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/* 4. Market Connect */
const MarketPortlet = () => {
  const [buyers] = useState([
    { id: 1, name: "Local Restaurant", demand: "50kg/week" },
    { id: 2, name: "Organic Store", demand: "30kg/week" },
  ]);
  return (
    <div>
      {buyers.map((b) => (
        <div
          key={b.id}
          className="bg-white p-3 rounded border flex justify-between mb-2"
        >
          <div>
            <h4 className="font-semibold">{b.name}</h4>
            <p className="text-sm text-gray-500">{b.demand}</p>
          </div>
          <button className="bg-green-600 text-white px-3 py-1 rounded">
            Contact
          </button>
        </div>
      ))}
    </div>
  );
};

/* 5. Training */
const TrainingPortlet = () => {
  const [courses] = useState([
    { id: 1, title: "Basics of Hydroponics", duration: "4 weeks" },
    { id: 2, title: "Advanced Vertical Farming", duration: "6 weeks" },
  ]);
  return (
    <div className="space-y-2">
      {courses.map((c) => (
        <div
          key={c.id}
          className="bg-white p-3 rounded border flex justify-between"
        >
          <div>
            <h4 className="font-semibold">{c.title}</h4>
            <p className="text-sm text-gray-500">{c.duration}</p>
          </div>
          <button className="bg-green-600 text-white px-3 py-1 rounded">
            Enroll
          </button>
        </div>
      ))}
    </div>
  );
};

/* 6. Yield Analytics */
const AnalyticsPortlet = () => {
  const [data] = useState([
    { crop: "Lettuce", yield: 120 },
    { crop: "Spinach", yield: 90 },
    { crop: "Kale", yield: 75 },
  ]);
  return (
    <div className="bg-white p-3 rounded">
      <h5 className="font-semibold mb-2">Yield Analytics (kg)</h5>
      <ul className="space-y-1">
        {data.map((d, idx) => (
          <li key={idx} className="flex justify-between">
            <span>{d.crop}</span>
            <span className="text-green-700 font-semibold">{d.yield}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* 7. Investment & ROI */
const InvestmentPortlet = () => {
  const [investments] = useState([
    { id: 1, item: "Hydroponic Setup", cost: 500000 },
    { id: 2, item: "LED Grow Lights", cost: 200000 },
  ]);
  const total = investments.reduce((s, i) => s + i.cost, 0);
  return (
    <div className="bg-white p-3 rounded">
      <h5 className="font-semibold">Investments</h5>
      {investments.map((i) => (
        <div key={i.id} className="flex justify-between py-1 border-b text-sm">
          <span>{i.item}</span>
          <span className="text-green-700">
            <FontAwesomeIcon icon={faRupeeSign} /> {i.cost}
          </span>
        </div>
      ))}
      <div className="text-right font-semibold mt-2">
        Total:{" "}
        <span className="text-green-700">
          <FontAwesomeIcon icon={faRupeeSign} /> {total}
        </span>
      </div>
    </div>
  );
};

/* 8. Leads */
const LeadsPortlet = () => {
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState({ name: "", contact: "", interest: "" });
  const submit = (e) => {
    e.preventDefault();
    setLeads((l) => [...l, { ...form, id: Date.now() }]);
    setForm({ name: "", contact: "", interest: "" });
    alert("Lead submitted (dummy)");
  };
  return (
    <div>
      <form
        onSubmit={submit}
        className="bg-gray-600 p-3 rounded border space-y-2"
      >
        <input
          required
          placeholder="Your name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full p-2 border rounded"
        />
        <input
          required
          placeholder="Contact (phone/email)"
          value={form.contact}
          onChange={(e) => setForm({ ...form, contact: e.target.value })}
          className="w-full p-2 border rounded"
        />
        <input
          required
          placeholder="Interest (partnership/training)"
          value={form.interest}
          onChange={(e) => setForm({ ...form, interest: e.target.value })}
          className="w-full p-2 border rounded"
        />
        <button className="w-full bg-green-600 text-white py-2 rounded">
          Submit
        </button>
      </form>

      <div className="mt-3 bg-white p-3 rounded border">
        <h5 className="font-semibold">Leads (dummy)</h5>
        {leads.length === 0 ? (
          <p className="text-sm text-gray-500">No leads yet.</p>
        ) : (
          leads.map((l) => (
            <div
              key={l.id}
              className="text-sm border-b py-1"
            >{`${l.name} • ${l.interest} • ${l.contact}`}</div>
          ))
        )}
      </div>
    </div>
  );
};

/* ----------------- Hub Container ----------------- */
export default function AgriLinkVerticalFarmingHub() {
  const [active, setActive] = useState(null);

  const useCases = [
    {
      id: "planner",
      title: "Crop Planner",
      desc: "Plan and track crop cycles",
      icon: faSeedling,
      component: <CropPlannerPortlet />,
    },
    {
      id: "iot",
      title: "IoT Monitoring",
      desc: "View live farm sensor data",
      icon: faIndustry,
      component: <IoTPortlet />,
    },
    {
      id: "nutrients",
      title: "Nutrient Management",
      desc: "Monitor and optimize nutrient levels",
      icon: faTint,
      component: <NutrientPortlet />,
    },
    {
      id: "market",
      title: "Market Connect",
      desc: "Connect with buyers & distributors",
      icon: faLeaf,
      component: <MarketPortlet />,
    },
    {
      id: "training",
      title: "Training Hub",
      desc: "Enroll in vertical farming courses",
      icon: faGraduationCap,
      component: <TrainingPortlet />,
    },
    {
      id: "analytics",
      title: "Yield Analytics",
      desc: "Analyze crop yield and performance",
      icon: faChartLine,
      component: <AnalyticsPortlet />,
    },
    {
      id: "investment",
      title: "Investment & ROI",
      desc: "Track setup cost and ROI",
      icon: faBoxOpen,
      component: <InvestmentPortlet />,
    },
    {
      id: "leads",
      title: "Leads",
      desc: "Capture and manage inquiries",
      icon: faHeadset,
      component: <LeadsPortlet />,
    },
  ];

  return (
    <div>
      <section className="min-h-screen bg-gradient-to-r from-green-700 to-green-800 py-12 px-4 md:px-10 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-yellow-200 flex items-center justify-center gap-3">
              <span className="text-3xl bg-green-100 text-yellow-200 rounded-full w-12 h-12 flex items-center justify-center">
                🌱
              </span>
              AgriLink Vertical Farming
            </h1>
            <p className="mt-3 text-white max-w-3xl mx-auto">
              Explore, learn, and grow with vertical farming solutions —
              technology-driven agriculture for the future.
            </p>
          </header>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {useCases.map((u) => (
              <Portlet
                key={u.id}
                icon={u.icon}
                title={u.title}
                desc={u.desc}
                expanded={active === u.id}
                onToggle={() => setActive((s) => (s === u.id ? null : u.id))}
              >
                {u.component}
                {/* <div className="mt-4 text-xs text-gray-500">
                  Tip: Click any card to expand.
                </div> */}
              </Portlet>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-green-700 text-white py-10 px-4 sm:px-6 md:px-20">
        <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
          <p className="text-sm">
            &copy; 2025 Think5Whys Global DeepTech Solutions. All rights
            reserved.
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0 text-sm">
            <Link
              to="/about-us"
              className="text-white hover:text-green-400 transition"
            >
              About
            </Link>
            <Link
              to="/privacy-policy"
              className="text-white hover:text-green-400 transition"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-use"
              className="text-white hover:text-green-400 transition"
            >
              Terms of Use
            </Link>
            <Link
              to="/contact-us"
              className="text-white hover:text-green-400 transition"
            >
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
